import React, { useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { getPremiumJob, getMe, searchProducts } from '../api/endpoints';
import Loading from '../components/Loading';
import { navigateTo } from '../utils/navigation';
import { motion, useReducedMotion, type Variants, type Transition } from 'framer-motion';
import type { PremiumJob, PremiumJobCondition, User, ProductSearchResultItem } from '../types/api';
import { DetailedFaceAnalysisModal } from '../components/DetailedFaceAnalysisModal';
import { FaceHeatmap } from '../components/FaceHeatmap';
import AIDisclaimerFooter from '../components/AIDisclaimerFooter';
import { premiumConditionLabel } from '../utils/conditionLabels';
import { skinToneLabel } from '../utils/skinTone';
import { useReportExport } from '../reports/useReportExport';

/* ── Brand palette hex — used where CSS vars can't reach (SVG, framer) ── */
const TEAL       = '#2AA4A6';
const DEEP_TEAL  = '#1A6F70';
const CORAL      = '#F4A88E';
const CHARCOAL12 = 'rgba(31,36,33,.12)';

/* ── Helpers ─────────────────────────────────────────────────────────── */

function getIcon(name: string): string {
    const n = name.toLowerCase();
    if (n.includes('acne') || n.includes('blemish'))           return 'vignette';
    if (n.includes('wrinkle') || n.includes('line'))            return 'waves';
    if (n.includes('circle') || n.includes('eye'))              return 'visibility';
    if (n.includes('texture') || n.includes('rough'))           return 'texture';
    if (n.includes('pigment') || n.includes('spot') || n.includes('sun')) return 'filter_tilt_shift';
    if (n.includes('hydrat') || n.includes('dry') || n.includes('oil'))   return 'water_drop';
    if (n.includes('pore'))                                     return 'drag_indicator';
    if (n.includes('bright') || n.includes('radia'))            return 'light_mode';
    if (n.includes('elast') || n.includes('firm'))              return 'gesture';
    return 'face';
}

function getProductIcon(t: string): string {
    const l = t.toLowerCase();
    if (l.includes('cleans'))                   return 'soap';
    if (l.includes('serum'))                    return 'opacity';
    if (l.includes('moistur'))                  return 'water_drop';
    if (l.includes('sun') || l.includes('spf')) return 'wb_sunny';
    if (l.includes('toner'))                    return 'filter_alt';
    if (l.includes('mask') || l.includes('peel')) return 'face_retouching_natural';
    if (l.includes('eye'))                      return 'visibility';
    return 'inventory_2';
}

function isPositiveMetric(name: string): boolean {
    const n = name.toLowerCase();
    return n.includes('hydrat') || n.includes('radia') || n.includes('elast') ||
           n.includes('firm') || n.includes('textur') || n.includes('smooth');
}

function isNone(val: string | undefined | null): boolean {
    if (!val) return true;
    const l = val.toLowerCase();
    return l === 'none' || l === 'not detected' || l === 'unknown';
}

/* Score stroke maps to brand palette hex values */
function scoreStroke(score: number): string {
    if (score >= 80) return DEEP_TEAL;
    if (score >= 50) return TEAL;
    return CORAL;
}

function scoreLabel(score: number): string {
    if (score >= 80) return 'Excellent';
    if (score >= 50) return 'Good';
    return 'Needs Care';
}

/* ── Sub-components ──────────────────────────────────────────────────── */

function FaceMapPreview({ job, filteredAnalysis, setShowFaceMap }: {
    job: PremiumJob;
    filteredAnalysis: PremiumJobCondition[];
    setShowFaceMap: (v: boolean) => void;
}) {
    const activeConditions = filteredAnalysis.map(c => c.condition_name);

    return (
        <div className="h-full flex flex-col group">
            <div
                className="relative w-full flex-grow bg-black cursor-pointer overflow-hidden"
                style={{
                    borderRadius: 'var(--r-xl)',
                    boxShadow: 'var(--sh-deep)',
                    border: '1px solid rgba(255,255,255,.06)',
                    minHeight: 300,
                }}
                role="button"
                tabIndex={0}
                onClick={() => { setShowFaceMap(true); }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowFaceMap(true);
                    }
                }}
            >
                <div className="absolute inset-0 z-0">
                    <FaceHeatmap
                        imagePath={job.image_path}
                        analysis={filteredAnalysis}
                        activeConditions={activeConditions}
                        enableBreathing={true}
                    />
                </div>

                {/* Hover HUD */}
                <div
                    className="absolute inset-x-4 top-4 z-10 opacity-0 group-hover:opacity-100"
                    style={{ transition: `opacity var(--t-slow) var(--ease)` }}
                >
                    <span className="pill pill--dark" style={{ fontSize: '9px', letterSpacing: 'var(--tracking-label)' }}>
                        Precision scan active
                    </span>
                </div>

                {/* Scrim */}
                <div
                    className="absolute inset-0 pointer-events-none group-hover:opacity-0"
                    style={{ background: 'rgba(0,0,0,.20)', transition: `opacity var(--t-slow) var(--ease)` }}
                />

                {/* Enlarge affordance */}
                <div
                    className="absolute bottom-6 right-6 z-10 opacity-0 group-hover:opacity-100"
                    style={{ transform: 'translateY(8px)', transition: `opacity var(--t-base) var(--ease), transform var(--t-base) var(--ease)` }}
                >
                    <div style={{
                        width: 48, height: 48, borderRadius: '50%',
                        background: 'rgba(255,255,255,.10)',
                        backdropFilter: 'blur(16px)',
                        border: '1px solid rgba(255,255,255,.18)',
                        display: 'grid', placeItems: 'center',
                        color: '#fff',
                        boxShadow: 'var(--sh-lifted)',
                    }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>open_in_full</span>
                    </div>
                </div>
            </div>

            <div className="row-3 justify-center" style={{ marginTop: 'var(--s-4)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: TEAL, display: 'inline-block' }} />
                <p className="t-label t-mute">Interactive Analysis Panel</p>
            </div>
        </div>
    );
}

function MetricCard({ icon, iconVariant, label, children }: {
    icon: string;
    iconVariant?: 'deep' | 'coral';
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className="metric-card">
            <div className={`metric-card__icon${iconVariant ? ` metric-card__icon--${iconVariant}` : ''}`}>
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>{icon}</span>
            </div>
            <div className="metric-card__body">
                <span className="metric-card__label">{label}</span>
                <p className="metric-card__value">{children}</p>
            </div>
        </div>
    );
}

function ConditionCard({ condition }: { condition: PremiumJobCondition }) {
    const isPositive = isPositiveMetric(condition.condition_name);
    // condition_name is the internal enum value; show the abstracted label.
    const displayName = premiumConditionLabel(condition.condition_name);
    const displayScore = condition.score <= 1
        ? Math.round(condition.score * 100)
        : Math.round(condition.score);

    const getSeverityClass = () => {
        const s = condition.severity.toLowerCase();
        if (isPositive) return 'severity-badge--low';
        if (s.includes('severe') || s.includes('high')) return 'severity-badge--high';
        if (s.includes('moderate') || s.includes('medium')) return 'severity-badge--medium';
        return 'severity-badge--low';
    };

    const getBarVariant = () => {
        const s = condition.severity.toLowerCase();
        if (isPositive) return 'result-bar--deep';
        if (s.includes('severe') || s.includes('high')) return 'result-bar--coral';
        return '';
    };

    return (
        <div className="condition-card h-full">
            <div>
                <div className="condition-card__head">
                    <div className="concern-dot concern-dot--teal">
                        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                            {getIcon(condition.condition_name)}
                        </span>
                    </div>
                    <span className={`severity-badge ${getSeverityClass()}`}>
                        {condition.severity}
                    </span>
                </div>
                <h3 className="condition-card__name">{displayName}</h3>
                <p className="condition-card__desc">{condition.description}</p>
            </div>
            <div className="condition-card__foot">
                <div className="condition-card__score-label">
                    <span className="t-label t-mute">{isPositive ? 'Quality' : 'Severity'}</span>
                    <span className="t-label t-teal">{displayScore}%</span>
                </div>
                <div className={`result-bar ${getBarVariant()}`}>
                    <motion.div
                        className="result-bar__fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${displayScore}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
                    />
                    <div className="result-bar__gloss" />
                </div>
            </div>
        </div>
    );
}

function ProductTypeCard({ type, onClick }: { type: string; onClick: () => void }) {
    return (
        <button className="product-type-card" onClick={onClick}>
            <div className="product-type-card__icon">
                <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>
                    {getProductIcon(type)}
                </span>
            </div>
            <div className="product-type-card__body">
                <p className="product-type-card__name">{type}</p>
                <p className="product-type-card__hint">Matched to your skin profile.</p>
            </div>
            <div className="product-type-card__arrow">
                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>arrow_forward</span>
            </div>
        </button>
    );
}

/* ── Page ────────────────────────────────────────────────────────────── */

export default function PremiumAnalysisResultPage(): React.JSX.Element {
  const { exporting, exportReport, errorToast } = useReportExport();
    const { id } = useParams<{ id: string }>();
    const [job, setJob] = useState<PremiumJob | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [showFaceMap, setShowFaceMap] = useState(false);

    const [selectedProductType, setSelectedProductType] = useState<string | null>(null);
    const [productResults, setProductResults] = useState<ProductSearchResultItem[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    const prefersReduced = useReducedMotion();
    const spring: Transition = prefersReduced
        ? { duration: 0 }
        : { type: 'spring', stiffness: 380, damping: 34, mass: 0.95 };
    const pageIn: Variants = {
        hidden: { opacity: 0, y: 12 },
        visible: { opacity: 1, y: 0, transition: { ...spring, duration: 0.4 } },
    };

    useEffect(() => {
        if (!id) return;
        Promise.all([
            getPremiumJob(id).then(res => res.job),
            getMe().catch(() => null),
        ]).then(([jobData, userData]) => {
            setJob(jobData);
            setUser(userData);
            setLoading(false);
        }).catch(() => { setLoading(false); });
    }, [id]);

    const handleProductClick = async (type: string) => {
        setSelectedProductType(type);
        setLoadingProducts(true);
        setProductResults([]);
        try {
            const results = await searchProducts({ product_keywords: type, limit: 10 });
            setProductResults(results);
        } catch {
            // silently fail — empty state shown
        } finally {
            setLoadingProducts(false);
        }
    };

    const filteredAnalysis = useMemo(() => {
        if (!job?.result?.detailed_analysis) return [];
        return job.result.detailed_analysis.filter(
            c => !isNone(c.condition_name) && !isNone(c.severity)
        );
    }, [job]);

    const filteredProducts = useMemo(() => {
        if (!job?.result?.recommendations?.recommended_product_types) return [];
        return job.result.recommendations.recommended_product_types.filter(p => !isNone(p));
    }, [job]);

    const skinAgeDiff = useMemo(() => {
        if (!job?.result || !user?.birthdate) return null;
        const birthDate = new Date(user.birthdate);
        if (Number.isNaN(birthDate.getTime())) return null;
        const actualAge = new Date().getFullYear() - birthDate.getFullYear();
        if (actualAge < 0 || actualAge > 120) return null;
        return job.result.summary_metrics.age_prediction - actualAge;
    }, [job, user]);

    if (loading) return <Loading />;

    if (!job?.result) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: 'var(--s-8)',
                textAlign: 'center', minHeight: '60vh',
            }}>
                <div className="icon-ring" style={{ marginBottom: 'var(--s-5)' }}>
                    <span className="material-symbols-outlined">sentiment_dissatisfied</span>
                </div>
                <h2 className="t-h2" style={{ margin: '0 0 var(--s-3)' }}>Report not found</h2>
                <p className="t-body t-mute" style={{ marginBottom: 'var(--s-6)', maxWidth: '40ch' }}>
                    We couldn&apos;t locate the analysis results for this session.
                </p>
                <button onClick={() => { navigateTo('/new'); }} className="btn btn--primary">
                    Start a new scan
                </button>
            </div>
        );
    }

    const { summary_metrics, recommendations } = job.result;
    const stroke = scoreStroke(summary_metrics.overall_score);
    const label  = scoreLabel(summary_metrics.overall_score);

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={pageIn}
            className="flex-1 overflow-y-auto"
            style={{ paddingBottom: 'var(--s-10)', maxWidth: 'var(--page-max)', marginInline: 'auto', width: '100%' }}
        >
            {/* ── Header ── */}
            <header
                className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
                style={{ marginTop: 'var(--s-3)', marginBottom: 'clamp(var(--s-4),5vw,var(--s-8))' }}
            >
                <div>
                    <h1 className="t-h2" style={{ margin: '0 0 var(--s-2)' }}>
                        Skin Analysis <em className="t-italic">Report</em>
                    </h1>
                    <div className="row-4" style={{ flexWrap: 'wrap' }}>
                        <span className="pill">#{job.id.slice(0, 8).toUpperCase()}</span>
                        <span className="t-small t-mute">
                            {new Date(job.submitted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                    </div>
                </div>
                <div className="row-4">
                    <button
                        onClick={() => { void exportReport('premium', job.id); }}
                        disabled={exporting}
                        className="btn btn--ghost btn--sm row-3"
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>download</span>
                        {exporting ? 'Preparing…' : 'Export'}
                    </button>
                    {errorToast}
                </div>
            </header>

            {/* ── Top card: face map | score ring | metrics ── */}
            <section style={{ marginBottom: 'clamp(var(--s-5),5vw,var(--s-9))' }}>
                <div
                    className="card card--xl card--glass"
                    style={{ overflow: 'hidden', border: '1px solid var(--rule)' }}
                >
                    <div
                        className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-stretch"
                        style={{ padding: 'clamp(var(--s-3),3vw,var(--s-5))' }}
                    >
                        {/* 1. Face map */}
                        <div className="col-span-1 h-full flex flex-col">
                            <p className="t-label t-mute" style={{ marginBottom: 'var(--s-5)' }}>Precision Map</p>
                            <div className="flex-1" style={{ minHeight: 300 }}>
                                <FaceMapPreview
                                    job={job}
                                    filteredAnalysis={filteredAnalysis}
                                    setShowFaceMap={setShowFaceMap}
                                />
                            </div>
                        </div>

                        {/* 2. Score ring */}
                        <div className="col-span-1 flex flex-col items-center justify-center" style={{ paddingBlock: 'clamp(var(--s-2),3vw,var(--s-7))', position: 'relative' }}>
                            {/* soft glow behind ring */}
                            <div style={{
                                position: 'absolute', top: '50%', left: '50%',
                                transform: 'translate(-50%,-50%)',
                                width: 192, height: 192, borderRadius: '50%',
                                background: stroke, filter: 'blur(60px)',
                                opacity: .08, pointerEvents: 'none',
                            }} />
                            <div className="relative" style={{ width: 192, height: 192 }}>
                                <svg className="w-full h-full" style={{ transform: 'rotate(-90deg)' }}>
                                    <circle
                                        cx="96" cy="96" r="82"
                                        fill="transparent"
                                        stroke={CHARCOAL12}
                                        strokeWidth="12"
                                    />
                                    <motion.circle
                                        initial={{ strokeDashoffset: 515 }}
                                        animate={{ strokeDashoffset: 515 - (515 * summary_metrics.overall_score) / 100 }}
                                        transition={{ duration: 1.5, ease: 'easeOut', delay: 0.2 }}
                                        cx="96" cy="96" r="82"
                                        fill="transparent"
                                        stroke={stroke}
                                        strokeLinecap="round"
                                        strokeWidth="12"
                                        style={{ strokeDasharray: 515 }}
                                    />
                                </svg>
                                <div
                                    className="absolute inset-0 flex flex-col items-center justify-center"
                                    style={{ textAlign: 'center' }}
                                >
                                    <span style={{ font: `400 48px/1 var(--f-display)`, letterSpacing: 'var(--tracking-display)', color: 'var(--ink)' }}>
                                        {summary_metrics.overall_score}
                                    </span>
                                    <span className="t-label t-mute" style={{ marginTop: 'var(--s-1)' }}>{label}</span>
                                </div>
                            </div>
                            <div style={{ marginTop: 'var(--s-3)', textAlign: 'center' }}>
                                <h3 className="t-h3" style={{ margin: '0 0 var(--s-1)' }}>Skin Index</h3>
                                <p className="t-small t-mute">Holistic skin appearance</p>
                            </div>
                        </div>

                        {/* 3. Key metrics */}
                        <div className="col-span-1 flex flex-col justify-center" style={{ gap: 'var(--s-3)' }}>
                            <MetricCard icon="water_drop" label="Skin Type">
                                {summary_metrics.skin_type || 'Combination'}
                            </MetricCard>
                            <MetricCard icon="face" iconVariant="deep" label="Face Shape">
                                {summary_metrics.face_shape || 'Oval'}
                            </MetricCard>
                            <MetricCard icon="palette" iconVariant="coral" label="Skin Tone">
                                {summary_metrics.skin_tone_hex && (
                                    <span
                                        className="metric-card__swatch"
                                        style={{ backgroundColor: summary_metrics.skin_tone_hex }}
                                    />
                                )}
                                {skinToneLabel(summary_metrics.fitzpatrick_scale)}
                            </MetricCard>
                            <MetricCard icon="cake" label="Predicted Age">
                                {summary_metrics.age_prediction ? `${summary_metrics.age_prediction} years` : 'N/A'}
                                {skinAgeDiff !== null && (
                                    <span className={`metric-card__badge${skinAgeDiff > 0 ? ' metric-card__badge--coral' : ''}`}>
                                        {skinAgeDiff < 0
                                            ? `${Math.abs(skinAgeDiff)}y younger`
                                            : skinAgeDiff > 0
                                            ? `+${skinAgeDiff}y`
                                            : 'Match'}
                                    </span>
                                )}
                            </MetricCard>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── AI Narrative ── */}
            {recommendations?.narrative && (
                <section style={{ marginBottom: 'clamp(var(--s-5),5vw,var(--s-9))', maxWidth: 900, marginInline: 'auto' }}>
                    <div
                        className="card card--xl"
                        style={{
                            background: 'var(--c-teal-weak)',
                            border: '1px solid rgba(42,164,166,.20)',
                            padding: 'clamp(var(--s-4),4vw,var(--s-7))',
                        }}
                    >
                        <h3
                            className="t-label"
                            style={{ marginBottom: 'var(--s-5)', display: 'flex', alignItems: 'center', gap: 8 }}
                        >
                            <span
                                aria-hidden="true"
                                style={{ width: 6, height: 6, borderRadius: '50%', background: TEAL, display: 'inline-block' }}
                            />
                            Skincare Insight
                        </h3>
                        <p style={{ font: `400 var(--fs-h3)/var(--leading-body) var(--f-text)`, color: 'var(--ink)', margin: 0 }}>
                            {recommendations.narrative}
                        </p>
                    </div>
                </section>
            )}

            {/* ── Detailed analysis ── */}
            <section style={{ marginBottom: 'clamp(var(--s-4),4vw,var(--s-8))' }}>
                <div
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between"
                    style={{ marginBottom: 'clamp(var(--s-3),3vw,var(--s-6))', gap: 'var(--s-4)' }}
                >
                    <h2 className="t-h2" style={{ margin: 0 }}>Detailed Analysis</h2>
                    <button
                        onClick={() => { setShowFaceMap(true); }}
                        className="btn btn--ghost btn--sm row-3"
                    >
                        View map
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_forward</span>
                    </button>
                </div>

                <motion.div
                    className="g-4"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-100px' }}
                    variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
                >
                    {filteredAnalysis.length > 0 ? filteredAnalysis.map((c, i) => (
                        <motion.div
                            key={i}
                            className="h-full"
                            variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                        >
                            <ConditionCard condition={c} />
                        </motion.div>
                    )) : (
                        <div
                            className="col-span-full card"
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--s-9)', textAlign: 'center' }}
                        >
                            <div className="concern-dot concern-dot--teal" style={{ width: 'var(--s-7)', height: 'var(--s-7)', marginBottom: 'var(--s-4)' }}>
                                <span className="material-symbols-outlined">check_circle</span>
                            </div>
                            <p style={{ font: `500 var(--fs-body)/1 var(--f-text)`, margin: 0 }}>
                                No specific concerns detected
                            </p>
                        </div>
                    )}
                </motion.div>
            </section>

            {/* ── Daily routine ── */}
            <section style={{ marginBottom: 'clamp(var(--s-5),5vw,var(--s-9))', maxWidth: 900, marginInline: 'auto' }}>
                <div
                    className="card card--xl card--glass"
                    style={{ border: '1px solid var(--rule)', padding: 'clamp(var(--s-4),4vw,var(--s-7))' }}
                >
                    <h3 className="t-label" style={{ marginBottom: 'clamp(var(--s-3),3vw,var(--s-7))' }}>Daily Skincare Protocol</h3>
                    <div className="relative">
                        {/* Connecting line */}
                        <div style={{
                            position: 'absolute', left: 13, top: 24, bottom: 0,
                            width: 1, background: 'var(--rule)',
                        }} />
                        <div className="stack-7">
                            {recommendations?.routine_steps.map((step, i) => (
                                <motion.div
                                    key={i}
                                    className="flex items-start"
                                    style={{ gap: 'var(--s-5)' }}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1, duration: 0.32 }}
                                >
                                    <div
                                        className="concern-dot concern-dot--teal"
                                        style={{ flexShrink: 0, position: 'relative', zIndex: 1 }}
                                    >
                                        <span style={{ fontFamily: 'var(--f-text)', fontSize: '10px', fontWeight: 600, color: TEAL }}>
                                            {i + 1}
                                        </span>
                                    </div>
                                    <div className="stack-2" style={{ paddingTop: 2, flex: 1 }}>
                                        <span className="pill pill--teal" style={{ alignSelf: 'flex-start' }}>Step {i + 1}</span>
                                        <h4 className="t-h3" style={{ margin: 0 }}>{step}</h4>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Recommended products ── */}
            <section style={{ marginBottom: 'clamp(var(--s-5),5vw,var(--s-9))', maxWidth: 900, marginInline: 'auto' }}>
                <h3 className="t-label" style={{ marginBottom: 'clamp(var(--s-3),3vw,var(--s-6))' }}>Recommended Essentials</h3>
                <motion.div
                    className="g-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
                >
                    {filteredProducts.length > 0 ? filteredProducts.map((p, i) => (
                        <motion.div
                            key={i}
                            variants={{ hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0 } }}
                        >
                            <ProductTypeCard type={p} onClick={() => handleProductClick(p)} />
                        </motion.div>
                    )) : (
                        <div
                            className="col-span-full card card--sand card--flat"
                            style={{ textAlign: 'center', padding: 'var(--s-9)' }}
                        >
                            <p className="t-small t-mute">No specific products required for this skin profile.</p>
                        </div>
                    )}
                </motion.div>
            </section>

            {/* ── Product search modal ── */}
            {selectedProductType && typeof document !== 'undefined' && ReactDOM.createPortal(
                <div className="modal" style={{ zIndex: 110 }}>
                    <div
                        className="modal__backdrop"
                        role="button"
                        tabIndex={0}
                        aria-label="Close dialog"
                        onClick={() => { setSelectedProductType(null); }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedProductType(null);
                            }
                        }}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.97, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.18, ease: [0.22, 0.61, 0.36, 1] }}
                        className="modal__card modal__card--lg"
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}
                    >
                        <div className="modal__header">
                            <h3 className="modal__title" style={{ textTransform: 'capitalize' }}>
                                {selectedProductType}
                            </h3>
                            <button
                                onClick={() => { setSelectedProductType(null); }}
                                className="btn btn--icon"
                                aria-label="Close"
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>close</span>
                            </button>
                        </div>
                        <div className="modal__scroll">
                            {loadingProducts ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s-9)' }}>
                                    <span
                                        className="material-symbols-outlined t-mute animate-spin"
                                        style={{ fontSize: '32px' }}
                                    >
                                        progress_activity
                                    </span>
                                </div>
                            ) : productResults.length > 0 ? (
                                <div className="stack-3">
                                    {productResults.map((product, idx) => (
                                        <a
                                            key={idx}
                                            href={product.product_link}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="product-result"
                                        >
                                            <div className="product-result__inner">
                                                {product.product_img_url ? (
                                                    <img
                                                        src={product.product_img_url}
                                                        alt={product.product_name}
                                                        className="product-result__img"
                                                        style={{ objectFit: 'contain' }}
                                                    />
                                                ) : (
                                                    <div className="product-result__img">
                                                        <span className="material-symbols-outlined">image</span>
                                                    </div>
                                                )}
                                                <div className="product-result__body">
                                                    <p className="product-result__name">{product.product_name}</p>
                                                    {product.product_price && (
                                                        <p className="product-result__price">
                                                            {typeof product.product_price === 'number'
                                                                ? `$${product.product_price}`
                                                                : product.product_price}
                                                        </p>
                                                    )}
                                                    <p className="product-result__desc">
                                                        {product.product_description ?? 'No description available.'}
                                                    </p>
                                                </div>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--s-9)', textAlign: 'center', gap: 'var(--s-4)' }}>
                                    <div className="icon-ring">
                                        <span className="material-symbols-outlined">inventory_2</span>
                                    </div>
                                    <p className="t-small t-mute">No products found for this category.</p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>,
                document.body
            )}

            {/* ── Detailed face map modal ── */}
            <DetailedFaceAnalysisModal
                isOpen={showFaceMap}
                onClose={() => { setShowFaceMap(false); }}
                imagePath={job.image_path}
                analysis={filteredAnalysis}
                jobId={job.id}
            />

            <AIDisclaimerFooter />
        </motion.div>
    );
}
